import { request } from 'utils/request';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';

import * as Types from 'types';
import { requestUsers } from 'features/users/api';

export { confirmBid } from './confirmBid';
export { matchBid } from './matchBid';

namespace Errors {
  export type NoCard = {
    error: string;
    error_code: 'error-no-card-present';
  };

  export type RequiresAction = {
    error: string;
    error_code: 'requires-action';
    error_variable: string;
  };

  export type HandledErrors = NoCard | RequiresAction;
}

export const isErrorNoCard = (data: any): data is Errors.NoCard => {
  return data?.error_code === 'error-no-card-present';
};

export const isErrorRequiresAction = (data: any): data is Errors.RequiresAction => {
  return data?.error_code === 'requires-action';
};

type PaymentMethodCard = {
  type: 'card';
  card: {
    last4: string;
  };
};

export const createBid = async (
  editionId: number,
  priceCents: number,
  options?: { dry?: boolean },
): Promise<{
  bid?: Types.Bid;
  error?: Errors.HandledErrors;
  paymentMethod?: PaymentMethodCard;
}> => {
  try {
    const payload = {
      edition_id: editionId,
      bid: { price_cents: priceCents },
      dry: Boolean(options?.dry),
    };

    if (payload.dry) {
      return await request
        .post<PaymentMethodCard>('/api/internal/bids', payload)
        .then(({ data }) => ({ paymentMethod: data }));
    } else {
      return await request
        .post<Types.Bid>('/api/internal/bids', payload)
        .then(({ data }) => ({ bid: data }));
    }
  } catch (error) {
    const { data } = (error as any).response;
    if (isErrorNoCard(data)) {
      return { error: data };
    } else if (isErrorRequiresAction(data)) {
      return { error: data };
    } else {
      throw error;
    }
  }
};

export const createBidUsingPaymentIntent = async (editionId: number, paymentIntentId: string) => {
  try {
    return await request.post('/api/internal/bids', {
      edition_id: editionId,
      payment_intent_id: paymentIntentId,
    });
  } catch (error) {
    bugsnag.notify(error);
    throw error;
  }
};

export type BidResponse = {
  id: number;
  priceCents: number;
  createdAt: number;
  userId: number;
  state: 'unauthorized' | 'new' | 'replaced' | 'matched' | 'error' | 'paid' | 'lost' | 'cancelled';
};

export const fetchBids = async (editionId: number): Promise<Types.Bid[]> => {
  const { data } = await request.get<BidResponse[]>(
    `/api/internal/editions/${editionId}/bids?per_page=100`,
  );
  const userIds = uniq(data.map((item) => item.userId));

  const users = await requestUsers({
    filter: 'ids',
    ids: userIds,
    perPage: 100,
  });
  const usersById = users.reduce<Record<number, Types.UserCard>>((obj, user) => {
    obj[user.id] = user;
    return obj;
  }, {});

  return sortBy(
    data.map((item) => ({
      ...item,
      createdAt: item.createdAt * 1000,
      user: usersById[item.userId],
    })),
    'priceCents',
  );
};
